import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ColumnState } from 'ag-grid-community'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import { SerializedGanttParameter } from '../../../../domain/value-object/GanttParameterVO'

export type StoredColumnState = Pick<
  ColumnState,
  'colId' | 'hide' | 'width' | 'pinned'
>
export const toStoredColumnState = (
  columnState: ColumnState
): StoredColumnState => {
  return {
    colId: columnState.colId,
    hide: columnState.hide,
    width: columnState.width,
    pinned: columnState.pinned,
  }
}

// ユーザー操作由来の列イベントのみ保存対象にする（自動・プログラム由来は除外）
export const columnEventValidSources = new Set([
  'toolPanelUi',
  'uiColumnMoved',
  'uiColumnResized',
  'uiColumnDragged',
])

export type StoredVersionReportPageDisplayState = {
  rowHeight?: number
  columnState?: StoredColumnState[]
  workloadUnit?: WorkloadUnit
  aggregateField?: AggregateField
  aggregateTarget?: AggregateTarget
  ganttParameter?: SerializedGanttParameter
}

export const DISPLAY_STATE_KEYS: (keyof StoredVersionReportPageDisplayState)[] =
  [
    'rowHeight',
    'columnState',
    'workloadUnit',
    'aggregateField',
    'aggregateTarget',
    'ganttParameter',
  ]

// 列リサイズのドラッグで update が高頻度に発火するため、Redis への書き込みは debounce する。
const PERSIST_DEBOUNCE_MS = 500

const EMPTY_DISPLAY_STATE: StoredVersionReportPageDisplayState = {}

export const useVersionReportPageDisplayState = (
  functionUuid: string,
  templatePageUuid: string | undefined
): {
  displayState: StoredVersionReportPageDisplayState
  updateDisplayState: (
    updated: Partial<StoredVersionReportPageDisplayState>
  ) => void
  // Redis から復元した時にのみ増えるトークン。columnState を「復元時だけ」
  // グリッドへ再適用するために使う (ユーザー編集の往復では再適用しない)。
  restoreToken: number
} => {
  const [displayStateValue, setDisplayStateValue] =
    useState<StoredVersionReportPageDisplayState>({})
  const [loadedKey, setLoadedKey] = useState<string | undefined>(undefined)
  const [restoreToken, setRestoreToken] = useState(0)

  const key = useMemo(
    () =>
      templatePageUuid
        ? `${UiStateKey.VersionReportDisplayState}-${templatePageUuid}`
        : undefined,
    [templatePageUuid]
  )

  const stateRef = useRef<StoredVersionReportPageDisplayState>({})
  const pendingRef = useRef<StoredVersionReportPageDisplayState | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const keyRef = useRef(key)
  const functionUuidRef = useRef(functionUuid)
  useEffect(() => {
    functionUuidRef.current = functionUuid
  }, [functionUuid])

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = undefined
    }
    const currentKey = keyRef.current
    if (currentKey && pendingRef.current) {
      uiStates.update(
        {
          key: currentKey,
          scope: UiStateScope.User,
          value: JSON.stringify(pendingRef.current),
        },
        functionUuidRef.current
      )
      pendingRef.current = null
    }
  }, [])

  useEffect(() => {
    flush()
    keyRef.current = key
    stateRef.current = {}
    if (!key) {
      setDisplayStateValue({})
      setLoadedKey(undefined)
      setRestoreToken(t => t + 1)
      return
    }
    let cancelled = false
    const restore = async () => {
      try {
        const response = await uiStates.get({
          applicationFunctionUuid: functionUuid,
          key,
          scope: UiStateScope.User,
        })
        if (cancelled) return
        if (pendingRef.current) return
        const stored: StoredVersionReportPageDisplayState = response.json.value
          ? JSON.parse(response.json.value)
          : {}
        stateRef.current = stored
        setDisplayStateValue(stored)
        setLoadedKey(key)
        setRestoreToken(t => t + 1)
      } catch (error) {
        console.error('Failed to restore version report display state:', error)
        if (!cancelled) {
          stateRef.current = {}
          setDisplayStateValue({})
          setLoadedKey(key)
          setRestoreToken(t => t + 1)
        }
      }
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [functionUuid, key, flush])

  useEffect(() => () => flush(), [flush])

  const updateDisplayState = useCallback(
    (updated: Partial<StoredVersionReportPageDisplayState>) => {
      if (!keyRef.current) return
      const next = { ...stateRef.current, ...updated }
      stateRef.current = next
      pendingRef.current = next
      setDisplayStateValue(next)
      setLoadedKey(keyRef.current)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(flush, PERSIST_DEBOUNCE_MS)
    },
    [flush]
  )

  const displayState =
    key !== undefined && loadedKey === key
      ? displayStateValue
      : EMPTY_DISPLAY_STATE

  return { displayState, updateDisplayState, restoreToken }
}
